import { BaseRepository, QueryOptions } from './BaseRepository';

/**
 * Import Job entity representing a SuccessWare import operation
 */
export interface ImportJob {
  id: string;
  company_id: string;
  job_type: string; // 'customers', 'service_accounts', 'locations', etc.
  status: 'pending' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  current_page: number;
  total_pages: number | null;
  records_imported: number;
  records_failed: number;
  batch_size: number;
  started_at: string | null;
  paused_at: string | null;
  completed_at: string | null;
  last_activity_at: string | null;
  error_message: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Import Batch entity representing a single batch within an import job
 */
export interface ImportBatch {
  id: string;
  company_id: string;
  import_type: string;
  batch_size: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  records_total: number;
  records_processed: number;
  records_successful: number;
  records_failed: number;
  started_at: string | null;
  completed_at: string | null;
  error_log: any | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Parameters for creating a new import job
 */
export interface CreateImportJobParams {
  company_id: string;
  job_type: string;
  batch_size?: number;
  total_pages?: number | null;
}

/**
 * Parameters for updating an import job
 */
export interface UpdateImportJobParams {
  status?: ImportJob['status'];
  current_page?: number;
  total_pages?: number | null;
  records_imported?: number;
  records_failed?: number;
  paused_at?: string | null;
  completed_at?: string | null;
  last_activity_at?: string | null;
  error_message?: string | null;
}

/**
 * Parameters for creating a new import batch
 */
export interface CreateImportBatchParams {
  company_id: string;
  import_type: string;
  batch_size: number;
  records_total: number;
}

/**
 * Parameters for updating an import batch
 */
export interface UpdateImportBatchParams {
  status?: ImportBatch['status'];
  records_processed?: number;
  records_successful?: number;
  records_failed?: number;
  started_at?: string | null;
  completed_at?: string | null;
  error_log?: any | null;
}

/**
 * Repository for managing SuccessWare import jobs and batches
 * 
 * This repository provides methods for:
 * - Creating and managing import jobs for different entity types
 * - Pausing and resuming imports (state persisted in database)
 * - Tracking batch processing progress
 * - Querying import history and status
 * 
 * The import system supports:
 * - Pausable/resumable imports that survive computer shutdown
 * - Batch processing (default 100 records per batch)
 * - Progress tracking with real-time updates
 * - Error handling and retry logic
 * 
 * @example
 * ```typescript
 * const repo = new SuccessWareImportRepository();
 * 
 * // Create a new import job
 * const job = await repo.createImportJob({
 *   company_id: 'company-123',
 *   job_type: 'customers',
 *   batch_size: 100
 * });
 * 
 * // Update progress
 * await repo.updateImportJob(job.id, {
 *   current_page: 5,
 *   records_imported: 500,
 *   last_activity_at: new Date().toISOString()
 * });
 * 
 * // Pause the job
 * await repo.pauseImportJob(job.id);
 * 
 * // Resume later (even after computer restart)
 * await repo.resumeImportJob(job.id);
 * ```
 */
export class SuccessWareImportRepository extends BaseRepository<ImportJob> {
  constructor() {
    super({ tableName: 'sw_import_jobs', primaryKey: 'id' });
  }

  /**
   * Create a new import job for a specific company and entity type
   * 
   * @param params Job creation parameters
   * @returns Promise resolving to the created job
   */
  async createImportJob(params: CreateImportJobParams): Promise<ImportJob> {
    const now = new Date().toISOString();
    
    const jobData: Partial<ImportJob> = {
      company_id: params.company_id,
      job_type: params.job_type,
      status: 'pending',
      current_page: 0,
      total_pages: params.total_pages || null,
      records_imported: 0,
      records_failed: 0,
      batch_size: params.batch_size || 100,
      started_at: null,
      paused_at: null,
      completed_at: null,
      last_activity_at: now,
      error_message: null,
    };

    return await this.create(jobData);
  }

  /**
   * Update an existing import job
   * 
   * @param id Job ID
   * @param params Update parameters
   * @returns Promise resolving to the updated job
   */
  async updateImportJob(id: string, params: UpdateImportJobParams): Promise<ImportJob> {
    const updates: Partial<ImportJob> = {
      ...params,
      last_activity_at: new Date().toISOString(),
    };

    return await this.update(id, updates);
  }

  /**
   * Get import jobs with optional filtering
   * 
   * @param options Query options
   * @returns Promise resolving to array of jobs
   */
  async getImportJobs(options?: QueryOptions): Promise<ImportJob[]> {
    return await this.findAll(options);
  }

  /**
   * Get a single import job by ID
   * 
   * @param id Job ID
   * @returns Promise resolving to the job or null if not found
   */
  async getImportJob(id: string): Promise<ImportJob | null> {
    return await this.findById(id);
  }

  /**
   * Pause a running import job
   * 
   * Saves the current state (current_page) to the database so the import
   * can be resumed later, even after computer shutdown.
   * 
   * @param id Job ID
   * @returns Promise resolving to the updated job
   */
  async pauseImportJob(id: string): Promise<ImportJob> {
    const now = new Date().toISOString();
    
    return await this.updateImportJob(id, {
      status: 'paused',
      paused_at: now,
    });
  }

  /**
   * Resume a paused import job
   * 
   * Loads the saved state from the database and continues from where it left off.
   * The current_page field maintains the position in the import.
   * 
   * @param id Job ID
   * @returns Promise resolving to the updated job
   */
  async resumeImportJob(id: string): Promise<ImportJob> {
    return await this.updateImportJob(id, {
      status: 'running',
      paused_at: null,
    });
  }

  /**
   * Get all active import jobs (running or pending)
   * 
   * @param company_id Optional company filter
   * @returns Promise resolving to array of active jobs
   */
  async getActiveImportJobs(company_id?: string): Promise<ImportJob[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .in('status', ['pending', 'running']);

    if (company_id) {
      query = query.eq('company_id', company_id);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch active import jobs: ${error.message}`);
    }

    return (data || []) as ImportJob[];
  }

  /**
   * Cancel an import job
   * 
   * @param id Job ID
   * @returns Promise resolving to the updated job
   */
  async cancelImportJob(id: string): Promise<ImportJob> {
    const now = new Date().toISOString();
    
    return await this.updateImportJob(id, {
      status: 'cancelled',
      completed_at: now,
    });
  }

  /**
   * Mark an import job as failed
   * 
   * @param id Job ID
   * @param error_message Error message describing the failure
   * @returns Promise resolving to the updated job
   */
  async failImportJob(id: string, error_message: string): Promise<ImportJob> {
    const now = new Date().toISOString();
    
    return await this.updateImportJob(id, {
      status: 'failed',
      error_message,
      completed_at: now,
    });
  }

  /**
   * Mark an import job as completed
   * 
   * @param id Job ID
   * @returns Promise resolving to the updated job
   */
  async completeImportJob(id: string): Promise<ImportJob> {
    const now = new Date().toISOString();
    
    return await this.updateImportJob(id, {
      status: 'completed',
      completed_at: now,
    });
  }

  /**
   * Increment the imported records count for a job
   * 
   * @param id Job ID
   * @param count Number of records successfully imported
   * @returns Promise resolving to the updated job
   */
  async incrementImportedRecords(id: string, count: number): Promise<ImportJob> {
    const job = await this.getImportJob(id);
    if (!job) {
      throw new Error(`Import job ${id} not found`);
    }

    return await this.updateImportJob(id, {
      records_imported: job.records_imported + count,
    });
  }

  /**
   * Increment the failed records count for a job
   * 
   * @param id Job ID
   * @param count Number of records that failed
   * @returns Promise resolving to the updated job
   */
  async incrementFailedRecords(id: string, count: number): Promise<ImportJob> {
    const job = await this.getImportJob(id);
    if (!job) {
      throw new Error(`Import job ${id} not found`);
    }

    return await this.updateImportJob(id, {
      records_failed: job.records_failed + count,
    });
  }

  /**
   * Get import jobs by company ID
   * 
   * @param company_id Company ID
   * @param options Additional query options
   * @returns Promise resolving to array of jobs
   */
  async getJobsByCompany(company_id: string, options?: Omit<QueryOptions, 'filters'>): Promise<ImportJob[]> {
    return await this.findAll({
      ...options,
      filters: { company_id },
    });
  }

  /**
   * Get import jobs by type
   * 
   * @param job_type Job type (e.g., 'customers', 'service_accounts')
   * @param options Additional query options
   * @returns Promise resolving to array of jobs
   */
  async getJobsByType(job_type: string, options?: Omit<QueryOptions, 'filters'>): Promise<ImportJob[]> {
    return await this.findAll({
      ...options,
      filters: { job_type },
    });
  }

  // ========== BATCH METHODS ==========

  /**
   * Create a new import batch
   * 
   * @param params Batch creation parameters
   * @returns Promise resolving to the created batch
   */
  async createBatch(params: CreateImportBatchParams): Promise<ImportBatch> {
    const batchData: Partial<ImportBatch> = {
      company_id: params.company_id,
      import_type: params.import_type,
      batch_size: params.batch_size,
      status: 'pending',
      records_total: params.records_total,
      records_processed: 0,
      records_successful: 0,
      records_failed: 0,
      started_at: null,
      completed_at: null,
      error_log: null,
    };

    const { data: created, error } = await this.client
      .from('sw_import_batches')
      .insert(batchData)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create import batch: ${error.message}`);
    }

    return created as ImportBatch;
  }

  /**
   * Update an existing import batch
   * 
   * @param id Batch ID
   * @param params Update parameters
   * @returns Promise resolving to the updated batch
   */
  async updateBatch(id: string, params: UpdateImportBatchParams): Promise<ImportBatch> {
    const { data: updated, error } = await this.client
      .from('sw_import_batches')
      .update(params)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update import batch: ${error.message}`);
    }

    return updated as ImportBatch;
  }

  /**
   * Get all batches for a specific job
   * 
   * @param job_id Job ID (matches company_id and import_type pattern)
   * @param company_id Company ID
   * @param import_type Import type
   * @returns Promise resolving to array of batches
   */
  async getJobBatches(company_id: string, import_type: string): Promise<ImportBatch[]> {
    const { data, error } = await this.client
      .from('sw_import_batches')
      .select('*')
      .eq('company_id', company_id)
      .eq('import_type', import_type)
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch job batches: ${error.message}`);
    }

    return (data || []) as ImportBatch[];
  }

  /**
   * Get a single batch by ID
   * 
   * @param id Batch ID
   * @returns Promise resolving to the batch or null if not found
   */
  async getBatch(id: string): Promise<ImportBatch | null> {
    const { data, error } = await this.client
      .from('sw_import_batches')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw new Error(`Failed to fetch batch: ${error.message}`);
    }

    return data as ImportBatch;
  }

  /**
   * Mark a batch as processing
   * 
   * @param id Batch ID
   * @returns Promise resolving to the updated batch
   */
  async startBatch(id: string): Promise<ImportBatch> {
    const now = new Date().toISOString();
    
    return await this.updateBatch(id, {
      status: 'processing',
      started_at: now,
    });
  }

  /**
   * Mark a batch as completed
   * 
   * @param id Batch ID
   * @returns Promise resolving to the updated batch
   */
  async completeBatch(id: string): Promise<ImportBatch> {
    const now = new Date().toISOString();
    
    return await this.updateBatch(id, {
      status: 'completed',
      completed_at: now,
    });
  }

  /**
   * Mark a batch as failed
   * 
   * @param id Batch ID
   * @param error_log Error information
   * @returns Promise resolving to the updated batch
   */
  async failBatch(id: string, error_log: any): Promise<ImportBatch> {
    const now = new Date().toISOString();
    
    return await this.updateBatch(id, {
      status: 'failed',
      completed_at: now,
      error_log,
    });
  }

  /**
   * Increment batch progress counters
   * 
   * @param id Batch ID
   * @param successful Number of successful records
   * @param failed Number of failed records
   * @returns Promise resolving to the updated batch
   */
  async incrementBatchProgress(
    id: string,
    successful: number,
    failed: number
  ): Promise<ImportBatch> {
    const batch = await this.getBatch(id);
    if (!batch) {
      throw new Error(`Batch ${id} not found`);
    }

    return await this.updateBatch(id, {
      records_processed: batch.records_processed + successful + failed,
      records_successful: batch.records_successful + successful,
      records_failed: batch.records_failed + failed,
    });
  }

  /**
   * Get batch statistics for a job
   * 
   * @param company_id Company ID
   * @param import_type Import type
   * @returns Promise resolving to batch statistics
   */
  async getBatchStats(company_id: string, import_type: string): Promise<{
    total_batches: number;
    completed_batches: number;
    failed_batches: number;
    total_records: number;
    successful_records: number;
    failed_records: number;
  }> {
    const batches = await this.getJobBatches(company_id, import_type);

    return {
      total_batches: batches.length,
      completed_batches: batches.filter(b => b.status === 'completed').length,
      failed_batches: batches.filter(b => b.status === 'failed').length,
      total_records: batches.reduce((sum, b) => sum + b.records_total, 0),
      successful_records: batches.reduce((sum, b) => sum + b.records_successful, 0),
      failed_records: batches.reduce((sum, b) => sum + b.records_failed, 0),
    };
  }
}
